import { env } from '@shared/config/env';
import type { LocationPlace, LocationSuggestion } from '../model/types';

// Two transport modes, decided once by whether the browser key is configured:
//  • KEY SET (`VITE_GOOGLE_MAPS_API_KEY`, referrer-restricted — same public-key model as the
//    mobile app): every call goes STRAIGHT to Google from the browser — Places API (New) at
//    places.googleapis.com (CORS-enabled by design) for nearby/autocomplete/details, and the
//    CORS-enabled legacy Geocoding endpoint for reverse geocoding. This is the only mode that
//    works on a DEPLOYED build (static hosting has no dev proxy).
//  • KEY EMPTY: the legacy Maps REST endpoints (which block browser CORS) are reached through
//    the Vite /maps-api dev proxy, which injects the key server-side — local dev only.
const KEY = env.VITE_GOOGLE_MAPS_API_KEY;
const useDirect = KEY !== '';

const PROXY_BASE = '/maps-api/maps/api';
const PLACES_V1 = 'https://places.googleapis.com/v1';
const GEOCODE_URL = useDirect
  ? 'https://maps.googleapis.com/maps/api/geocode/json'
  : `${PROXY_BASE}/geocode/json`;
const GEOLOCATE_URL = `https://www.googleapis.com/geolocation/v1/geolocate?key=${KEY}`;
const NEARBY_URL = `${PROXY_BASE}/place/nearbysearch/json`;
const AUTOCOMPLETE_URL = `${PROXY_BASE}/place/autocomplete/json`;
const DETAILS_URL = `${PROXY_BASE}/place/details/json`;

const NEARBY_RADIUS_M = 500;
const NEARBY_LIMIT = 10;
const AUTOCOMPLETE_BIAS_RADIUS_M = 50_000;
/** Mobile distance-tier boundary: ≤500 m suggestions sort before ≤50 km ones. */
const SUGGESTION_NEAR_TIER_M = 500;

interface AddressComponent {
  readonly long_name: string;
  readonly types: readonly string[];
}

/** Straight-line distance in meters between two lat/long points (Haversine). */
function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Split a Places `address_components` array into the fields the expense payload wants.
 * Mirrors the Flutter `_updateAddress`/`_attachLocation` parse: street_number+route → address1,
 * sublocality → address2, locality → city, admin_area_level_1 → state, postal_code → zip.
 */
function parseAddressComponents(components: readonly AddressComponent[]): {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
} {
  let streetNumber = '';
  let route = '';
  let address2 = '';
  let city = '';
  let state = '';
  let zip = '';
  let country = '';
  for (const c of components) {
    if (c.types.includes('street_number')) streetNumber = c.long_name;
    else if (c.types.includes('route')) route = c.long_name;
    else if (c.types.includes('sublocality') || c.types.includes('sublocality_level_1')) {
      address2 = c.long_name;
    } else if (c.types.includes('locality')) city = c.long_name;
    else if (c.types.includes('administrative_area_level_1')) state = c.long_name;
    else if (c.types.includes('postal_code')) zip = c.long_name;
    else if (c.types.includes('country')) country = c.long_name;
  }
  const address1 = [streetNumber, route].filter(Boolean).join(' ').trim();
  return { address1, address2, city, state, zip, country };
}

/** A named place (POI/premise/establishment) from `address_components`, else null. */
function placeNameFromComponents(components: readonly AddressComponent[]): string | null {
  for (const c of components) {
    if (
      c.types.includes('point_of_interest') ||
      c.types.includes('premise') ||
      c.types.includes('establishment')
    ) {
      return c.long_name;
    }
  }
  return null;
}

/**
 * A Google PLUS CODE token ("2HHH+Q38", "7JVW2HHH+Q38") — the alphabet is Google's fixed
 * open-location-code set. Never a human-meaningful place name.
 */
const PLUS_CODE_RE = /^[23456789CFGHJMPQRVWX]{2,8}\+[23456789CFGHJMPQRVWX]{2,}$/i;

/**
 * Display name for a place with no POI name: the formatted address's first segment — unless that
 * is a bare house/plot number (e.g. "732") or a Plus Code ("2HHH+Q38"), which alone are
 * meaningless on the location chip. A house number keeps its street line (address1, "732 Lal
 * Bahadur Shastri Marg") or widens to two segments; a Plus Code is skipped outright in favor of
 * the first real segment after it ("2HHH+Q38, Old City, Khanpur…" → "Old City").
 */
function nameFromAddress(formattedAddress: string, address1: string): string {
  const segments = formattedAddress.split(',').map((s) => s.trim());
  const first = segments[0] ?? '';
  if (PLUS_CODE_RE.test(first)) {
    return segments.find((s) => s !== '' && !PLUS_CODE_RE.test(s) && !/^\d+$/.test(s)) ?? first;
  }
  if (!/^\d+$/.test(first)) return first;
  if (address1 && address1 !== first) return address1;
  const second = segments[1] ?? '';
  return second ? `${first}, ${second}` : first;
}

function toLocationPlace(
  lat: number,
  long: number,
  formattedAddress: string,
  components: readonly AddressComponent[],
  fallbackName?: string,
): LocationPlace {
  const parsed = parseAddressComponents(components);
  const name =
    placeNameFromComponents(components) ??
    fallbackName ??
    (nameFromAddress(formattedAddress, parsed.address1) || formattedAddress);
  return { name, formattedAddress, ...parsed, lat, long };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

// `Array.isArray` alone narrows an `unknown` property to `any[]` (a known TS lib quirk), which
// trips `no-unsafe-assignment` at every call site — funnel through this helper instead so callers
// get a properly-typed `unknown[]`.
function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** GET a JSON body, `null` on network/HTTP failure (callers treat null as "no results"). */
async function getJson(url: string, headers?: Record<string, string>): Promise<unknown> {
  try {
    const res = await fetch(url, headers ? { headers } : undefined);
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

/** POST a JSON body to a Places API (New) endpoint (browser key in the header), `null` on failure. */
async function postPlacesV1(path: string, body: unknown, fieldMask?: string): Promise<unknown> {
  try {
    const res = await fetch(`${PLACES_V1}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': KEY,
        ...(fieldMask ? { 'X-Goog-FieldMask': fieldMask } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

/** Reverse-geocode a lat/long into a full `LocationPlace` (used for GPS + "use current location"). */
export async function reverseGeocode(lat: number, long: number): Promise<LocationPlace | null> {
  const key = useDirect ? `&key=${KEY}` : '';
  const data = await getJson(`${GEOCODE_URL}?latlng=${lat},${long}${key}`);
  const results = (isRecord(data) ? asArray(data.results) : []).filter(isRecord);
  // Google often lists a PLUS-CODE pseudo-address first for points with no exact street
  // address — taking it verbatim showed "2HHH+Q38" as the selected place. Prefer the first
  // REAL address result; fall back to the plus-code row only when nothing else came back.
  const first = results.find((r) => !asArray(r.types).includes('plus_code')) ?? results[0];
  if (!isRecord(first)) return null;
  const formatted = typeof first.formatted_address === 'string' ? first.formatted_address : '';
  const components = Array.isArray(first.address_components)
    ? (first.address_components as AddressComponent[])
    : [];
  return toLocationPlace(lat, long, formatted, components);
}

/**
 * Approximate device position from the network — the last-resort fallback when the browser
 * Geolocation API can't produce a fix: no GPS hardware, or (the case that actually reaches this on
 * a deployed environment) the page's `Permissions-Policy` header disables `geolocation` outright,
 * so the browser never even shows its permission prompt (see docs/architecture/security.md).
 * City-level accuracy at best.
 *
 * Tries Google's Geolocation API first when a direct browser key is configured (the endpoint lives
 * on www.googleapis.com, which the /maps-api proxy doesn't reach, so it needs its own key), then
 * falls through to KEYLESS public IP-lookup services that need no configuration at all. That keyless
 * step is what makes this resolve on EVERY deployment — including one with no Maps browser key
 * baked into the build, or one whose server blocks the Geolocation permission — rather than
 * silently returning null and leaving "current location" unset.
 */
export async function geolocateByIp(): Promise<{ lat: number; long: number } | null> {
  if (useDirect) {
    const viaGoogle = await geolocateByIpGoogle();
    if (viaGoogle) return viaGoogle;
  }
  return geolocateByIpKeyless();
}

async function geolocateByIpGoogle(): Promise<{ lat: number; long: number } | null> {
  try {
    const res = await fetch(GEOLOCATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ considerIp: true }),
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const loc = isRecord(data) && isRecord(data.location) ? data.location : null;
    const lat = typeof loc?.lat === 'number' ? loc.lat : null;
    const lng = typeof loc?.lng === 'number' ? loc.lng : null;
    return lat !== null && lng !== null ? { lat, long: lng } : null;
  } catch {
    return null;
  }
}

/** ipapi.co — HTTPS, CORS-enabled, no API key required for this app's request volume. */
async function geolocateViaIpapiCo(): Promise<{ lat: number; long: number } | null> {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const lat = isRecord(data) && typeof data.latitude === 'number' ? data.latitude : null;
    const long = isRecord(data) && typeof data.longitude === 'number' ? data.longitude : null;
    return lat !== null && long !== null ? { lat, long } : null;
  } catch {
    return null;
  }
}

/** ipwho.is — HTTPS, CORS-enabled, no API key. A second keyless provider so one outage doesn't
 *  take the whole fallback down. */
async function geolocateViaIpwhoIs(): Promise<{ lat: number; long: number } | null> {
  try {
    const res = await fetch('https://ipwho.is/');
    if (!res.ok) return null;
    const data: unknown = await res.json();
    if (!isRecord(data) || data.success === false) return null;
    const lat = typeof data.latitude === 'number' ? data.latitude : null;
    const long = typeof data.longitude === 'number' ? data.longitude : null;
    return lat !== null && long !== null ? { lat, long } : null;
  } catch {
    return null;
  }
}

/** Tried in order; the first provider to resolve a position wins. */
const KEYLESS_IP_PROVIDERS = [geolocateViaIpapiCo, geolocateViaIpwhoIs] as const;

async function geolocateByIpKeyless(): Promise<{ lat: number; long: number } | null> {
  for (const provider of KEYLESS_IP_PROVIDERS) {
    const result = await provider();
    if (result) return result;
  }
  return null;
}

interface NearbyResult {
  place: LocationPlace;
  dist: number;
}

/** Shared post-processing for both nearby transports: distance re-filter (Google can return
 *  slightly out-of-radius rows), nearest-first sort, cap. */
function finalizeNearby(rows: readonly (NearbyResult | null)[]): LocationPlace[] {
  return rows
    .filter((v): v is NearbyResult => v !== null)
    .sort((a, b) => a.dist - b.dist)
    .slice(0, NEARBY_LIMIT)
    .map((v) => v.place);
}

function toNearbyResult(
  originLat: number,
  originLong: number,
  placeLat: number | null,
  placeLng: number | null,
  name: string,
  vicinity: string,
): NearbyResult | null {
  if (placeLat === null || placeLng === null) return null;
  const dist = distanceMeters(originLat, originLong, placeLat, placeLng);
  if (dist > NEARBY_RADIUS_M) return null;
  const formattedAddress = vicinity ? `${name}, ${vicinity}` : name;
  return {
    place: {
      name: name || vicinity,
      formattedAddress,
      address1: '',
      address2: vicinity,
      city: '',
      state: '',
      zip: '',
      country: '',
      lat: placeLat,
      long: placeLng,
      distanceMeters: Math.round(dist),
    },
    dist,
  };
}

/** Places within `NEARBY_RADIUS_M` of the origin, nearest first, capped at `NEARBY_LIMIT`. */
export async function nearbySearch(lat: number, long: number): Promise<LocationPlace[]> {
  if (useDirect) {
    const data = await postPlacesV1(
      '/places:searchNearby',
      {
        maxResultCount: NEARBY_LIMIT,
        locationRestriction: {
          circle: { center: { latitude: lat, longitude: long }, radius: NEARBY_RADIUS_M },
        },
      },
      'places.displayName,places.shortFormattedAddress,places.location',
    );
    const places = isRecord(data) ? asArray(data.places) : [];
    return finalizeNearby(
      places.filter(isRecord).map((place) => {
        const loc = isRecord(place.location) ? place.location : null;
        const placeLat = typeof loc?.latitude === 'number' ? loc.latitude : null;
        const placeLng = typeof loc?.longitude === 'number' ? loc.longitude : null;
        const display = isRecord(place.displayName) ? place.displayName : null;
        const name = typeof display?.text === 'string' ? display.text : '';
        // `shortFormattedAddress` is the New-API analog of the legacy `vicinity`.
        const vicinity =
          typeof place.shortFormattedAddress === 'string' ? place.shortFormattedAddress : '';
        return toNearbyResult(lat, long, placeLat, placeLng, name, vicinity);
      }),
    );
  }

  const data = await getJson(`${NEARBY_URL}?location=${lat},${long}&radius=${NEARBY_RADIUS_M}`);
  const results = isRecord(data) ? asArray(data.results) : [];
  return finalizeNearby(
    results.filter(isRecord).map((place) => {
      const loc =
        isRecord(place.geometry) && isRecord(place.geometry.location)
          ? place.geometry.location
          : null;
      const placeLat = typeof loc?.lat === 'number' ? loc.lat : null;
      const placeLng = typeof loc?.lng === 'number' ? loc.lng : null;
      const name = typeof place.name === 'string' ? place.name : '';
      const vicinity = typeof place.vicinity === 'string' ? place.vicinity : '';
      return toNearbyResult(lat, long, placeLat, placeLng, name, vicinity);
    }),
  );
}

/** Mobile distance tiers: ≤500 m first, then anything with a distance, then global (no distance). */
function suggestionTier(s: LocationSuggestion): number {
  if (s.distanceMeters === undefined) return 2;
  return s.distanceMeters <= SUGGESTION_NEAR_TIER_M ? 0 : 1;
}

/**
 * Autocomplete predictions, biased to the origin within `AUTOCOMPLETE_BIAS_RADIUS_M`. Falls back to
 * an unbiased search when the biased query returns nothing, and orders results by the mobile
 * distance tiers (nearest tier first, by distance within a tier) — both mirror the Flutter picker.
 */
export async function autocomplete(
  input: string,
  origin?: { lat: number; long: number },
): Promise<LocationSuggestion[]> {
  const biased = await fetchAutocomplete(input, origin);
  const results = biased.length > 0 || !origin ? biased : await fetchAutocomplete(input);
  return [...results].sort(
    (a, b) =>
      suggestionTier(a) - suggestionTier(b) || (a.distanceMeters ?? 0) - (b.distanceMeters ?? 0),
  );
}

async function fetchAutocomplete(
  input: string,
  origin?: { lat: number; long: number },
): Promise<LocationSuggestion[]> {
  if (useDirect) {
    const data = await postPlacesV1('/places:autocomplete', {
      input,
      ...(origin
        ? {
            // The bias makes nearby matches rank first; `origin` makes Google return
            // `distanceMeters` per prediction (drives the distance tiers/labels).
            locationBias: {
              circle: {
                center: { latitude: origin.lat, longitude: origin.long },
                radius: AUTOCOMPLETE_BIAS_RADIUS_M,
              },
            },
            origin: { latitude: origin.lat, longitude: origin.long },
          }
        : {}),
    });
    const suggestions = isRecord(data) ? asArray(data.suggestions) : [];
    return suggestions
      .filter(isRecord)
      .map((s): LocationSuggestion => {
        const p = isRecord(s.placePrediction) ? s.placePrediction : null;
        const structured = p && isRecord(p.structuredFormat) ? p.structuredFormat : null;
        const mainRec = structured && isRecord(structured.mainText) ? structured.mainText : null;
        const secondaryRec =
          structured && isRecord(structured.secondaryText) ? structured.secondaryText : null;
        const textRec = p && isRecord(p.text) ? p.text : null;
        const distM = p && typeof p.distanceMeters === 'number' ? p.distanceMeters : undefined;
        return {
          placeId: p && typeof p.placeId === 'string' ? p.placeId : '',
          mainText:
            (typeof mainRec?.text === 'string' ? mainRec.text : null) ??
            (typeof textRec?.text === 'string' ? textRec.text : ''),
          secondaryText: typeof secondaryRec?.text === 'string' ? secondaryRec.text : '',
          ...(distM !== undefined ? { distanceMeters: distM } : {}),
        };
      })
      .filter((s) => s.placeId !== '');
  }

  const bias = origin
    ? `&location=${origin.lat},${origin.long}&radius=${AUTOCOMPLETE_BIAS_RADIUS_M}`
    : '';
  const data = await getJson(`${AUTOCOMPLETE_URL}?input=${encodeURIComponent(input)}${bias}`);
  const predictions = isRecord(data) ? asArray(data.predictions) : [];

  return predictions.filter(isRecord).map((p) => {
    const structured = isRecord(p.structured_formatting) ? p.structured_formatting : null;
    const distM = typeof p.distance_meters === 'number' ? p.distance_meters : undefined;
    return {
      placeId: typeof p.place_id === 'string' ? p.place_id : '',
      mainText:
        (typeof structured?.main_text === 'string' ? structured.main_text : null) ??
        (typeof p.description === 'string' ? p.description : ''),
      secondaryText:
        typeof structured?.secondary_text === 'string' ? structured.secondary_text : '',
      ...(distM !== undefined ? { distanceMeters: distM } : {}),
    };
  });
}

/** Resolve an autocomplete suggestion's `placeId` into a full `LocationPlace`. */
export async function placeDetails(placeId: string): Promise<LocationPlace | null> {
  if (useDirect) {
    const data = await getJson(`${PLACES_V1}/places/${placeId}`, {
      'X-Goog-Api-Key': KEY,
      'X-Goog-FieldMask': 'displayName,formattedAddress,addressComponents,location',
    });
    if (!isRecord(data)) return null;
    const loc = isRecord(data.location) ? data.location : null;
    const lat = typeof loc?.latitude === 'number' ? loc.latitude : null;
    const long = typeof loc?.longitude === 'number' ? loc.longitude : null;
    if (lat === null || long === null) return null;
    const formatted = typeof data.formattedAddress === 'string' ? data.formattedAddress : '';
    // New-API components carry `longText`; adapt to the legacy shape the shared parser reads.
    const components: AddressComponent[] = asArray(data.addressComponents)
      .filter(isRecord)
      .map((c) => ({
        long_name: typeof c.longText === 'string' ? c.longText : '',
        types: asArray(c.types).filter((v): v is string => typeof v === 'string'),
      }));
    const display = isRecord(data.displayName) ? data.displayName : null;
    const fallbackName = typeof display?.text === 'string' ? display.text : undefined;
    return toLocationPlace(lat, long, formatted, components, fallbackName);
  }

  const fields = 'geometry,formatted_address,address_components,name';
  const data = await getJson(`${DETAILS_URL}?place_id=${placeId}&fields=${fields}`);
  const result = isRecord(data) ? data.result : null;
  if (!isRecord(result)) return null;
  const loc =
    isRecord(result.geometry) && isRecord(result.geometry.location)
      ? result.geometry.location
      : null;
  const lat = typeof loc?.lat === 'number' ? loc.lat : null;
  const long = typeof loc?.lng === 'number' ? loc.lng : null;
  if (lat === null || long === null) return null;
  const formatted = typeof result.formatted_address === 'string' ? result.formatted_address : '';
  const components = Array.isArray(result.address_components)
    ? (result.address_components as AddressComponent[])
    : [];
  const fallbackName = typeof result.name === 'string' ? result.name : undefined;
  return toLocationPlace(lat, long, formatted, components, fallbackName);
}
